import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    Checkbox,
    Grid,
    Hidden,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CustomSelect from "components/customSelect/customSelect";
import YesNoPrompt from "components/modals/prompts/yesNoPrompt";
import { ATTACHMENT_RECIPIENTS } from "constants/generalConstants";
import useAttachments from "hooks/useAttachments";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
    setAttachmentRecipients,
    toggleAttachmentsSelectionForDeletion,
} from "redux/actions/attachmentActions";

const { default: React, useState, useRef } = require("react");

const AttachmentTable = ({ attachments }) => {
    const [openDeletePopup, setOpenDeletePopup] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const { deleteAttachment } = useAttachments();
    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );

    const dispatch = useDispatch();

    const handleDeleteClick = (attachment) => {
        setAttachmentToDelete(attachment);
        setOpenDeletePopup(true);
    };

    const handleConfirmDeleteNo = () => {
        setOpenDeletePopup(false);
        //setAttachmentToDelete(null);
    };

    const handleConfirmDeleteYes = async () => {
        setOpenDeletePopup(false);
        await deleteAttachment(attachmentToDelete, dispatch);
        setAttachmentToDelete(null);
    };

    // This method gets called on header check box is selected/unselected
    const toggleSelectAllClick = (event) => {
        const newSelectedAttachmentIds = attachments.map(
            (file) => file.ATTACHMENT_ID
        );

        let selectedValue = event.target.checked;

        // If the current header check box state is indeterminate, and user clicks on the check box, it turns out to be fully checked,
        // but in the POST PO amendment old files can't be selected, hence check box needs to be unchecked
        if (
            isPostPoReq &&
            isIndeterminateState &&
            newUploadedFilesLength === selectedAttachmentsLength &&
            newUploadedFilesLength > 0
        ) {
            selectedValue = false;
        }

        dispatch(
            toggleAttachmentsSelectionForDeletion(
                newSelectedAttachmentIds,
                selectedValue,
                isPostPoReq
            )
        );
    };

    // This method gets called on individual row selected/unselected
    const toggleSelectClick = (event, attachmentId, isSelected) => {
        dispatch(
            toggleAttachmentsSelectionForDeletion([attachmentId], isSelected)
        );
    };

    const handleRecipientsSelection = (attachmentId, selectedRecipients) => {
        dispatch(setAttachmentRecipients(attachmentId, selectedRecipients));
    };

    const attachmentsLength = attachments?.length;

    const selectedAttachmentsLength = attachments.filter(
        (item) => item.isSelectedToDelete === true
    ).length;

    const newUploadedFilesLength = attachments.filter(
        (a) => a.isNewUpload
    ).length;

    const isIndeterminateState =
        attachmentsLength > 0 &&
        attachmentsLength > selectedAttachmentsLength &&
        selectedAttachmentsLength != 0;

    return (
        <>
            <Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    padding="checkbox"
                                    sx={{ width: "5%" }}
                                >
                                    <Checkbox
                                        onClick={toggleSelectAllClick}
                                        indeterminate={isIndeterminateState}
                                        checked={
                                            attachmentsLength > 0 &&
                                            attachmentsLength ===
                                                selectedAttachmentsLength
                                        }
                                    />
                                </TableCell>
                                <TableCell sx={{ width: "40%" }}>
                                    <Typography variant="h6" display="block">
                                        File Details
                                    </Typography>
                                </TableCell>
                                <Hidden xsDown>
                                    <TableCell sx={{ width: "40%" }}>
                                        <Typography
                                            variant="h6"
                                            display="block"
                                        >
                                            Recipients
                                        </Typography>
                                    </TableCell>
                                </Hidden>
                                <TableCell sx={{ width: "8%" }}>
                                    <Typography variant="h6" display="block">
                                        Delete
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attachments.map((file, index) => {
                                // If the attachment is not new upload then it cannot be deleted during the post po amendment
                                const isFileNotAmendable =
                                    isPostPoReq && !file.isNewUpload;

                                return (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            paddingY: 2,
                                            borderBottom: "1px solid",
                                            borderColor: "grey.300",
                                            mb: 2,
                                            // backgroundColor: "#2e3037",
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={
                                                    file.isSelectedToDelete &&
                                                    !isFileNotAmendable
                                                }
                                                onChange={(event) => {
                                                    toggleSelectClick(
                                                        event,
                                                        file.ATTACHMENT_ID,
                                                        isFileNotAmendable
                                                            ? false
                                                            : !file.isSelectedToDelete
                                                    );
                                                }}
                                                disabled={isFileNotAmendable}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems={"center"}
                                            >
                                                <AttachFileIcon
                                                    sx={{
                                                        color: isFileNotAmendable
                                                            ? "grey.500"
                                                            : "inherit",
                                                    }}
                                                />
                                                <Stack>
                                                    <Typography
                                                        variant="body2"
                                                        color={
                                                            isFileNotAmendable
                                                                ? "text.secondary"
                                                                : "inherit"
                                                        }
                                                        style={{
                                                            overflowWrap:
                                                                "break-word",
                                                            wordBreak:
                                                                "break-all",
                                                            whiteSpace:
                                                                "normal",
                                                        }}
                                                    >
                                                        {file.FILENAME}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {`Uploaded on 
                                                        ${
                                                            file.CREATED_DATE
                                                                ? new Date(
                                                                      file?.CREATED_DATE
                                                                  ).toLocaleString(
                                                                      "en-GB"
                                                                  )
                                                                : ""
                                                        }`}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </TableCell>
                                        <Hidden xsDown>
                                            <TableCell>
                                                <CustomSelect
                                                    options={
                                                        ATTACHMENT_RECIPIENTS
                                                    }
                                                    initialSelectedValues={
                                                        file.CATEGORY
                                                    }
                                                    onSelectionChange={(
                                                        selectedValues
                                                    ) => {
                                                        handleRecipientsSelection(
                                                            file.ATTACHMENT_ID,
                                                            selectedValues
                                                        );
                                                    }}
                                                    disableSelection={
                                                        isFileNotAmendable
                                                    }
                                                />
                                            </TableCell>
                                        </Hidden>
                                        <TableCell>
                                            <Tooltip
                                                title={
                                                    isFileNotAmendable
                                                        ? "Files cannot be deleted after a Purchase Order has been created."
                                                        : `Delete ${file.TITLE} attachment`
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        disabled={
                                                            isFileNotAmendable
                                                        }
                                                        onClick={() =>
                                                            handleDeleteClick(
                                                                file
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon
                                                            color={
                                                                isFileNotAmendable
                                                                    ? "disabled"
                                                                    : "error"
                                                            }
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <YesNoPrompt
                title="Delete Confirmation"
                subtitle={`Are you sure you want to delete ${attachmentToDelete?.TITLE}
                                            attachment?`}
                promptOpen={openDeletePopup}
                setPromptOpen={handleConfirmDeleteNo}
                onClickYes={() => {
                    handleConfirmDeleteYes();
                }}
            />
        </>
    );
};

AttachmentTable.propTypes = {
    attachments: PropTypes.array,
};

export default AttachmentTable;
